import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit2 } from "iconsax-react";
import { Button, Input, Prompt } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useUpdateProjectMutation } from "@/features/projects";

interface Props {
  projectId: number;
  description: string;
  canEdit: boolean;
}

export default function DescriptionSection({ projectId, description, canEdit }: Props) {
  const { t } = useTranslation("BoardPage");
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [updateProject] = useUpdateProjectMutation();

  function handleEditClick() {
    setEditedText(description);
    setIsEditing(true);
  }

  function handleCancel() {
    setIsEditing(false);
    setEditedText(description);
  }

  async function handleSave() {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await updateProject({ id: projectId, data: { description: editedText } }).unwrap();
      setIsEditing(false);
      setPrompt({
        title: t("overview.description.successTitle"),
        text: t("overview.description.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("overview.description.errorTitle"),
        text: extractErrorMessage(err, "overview.description.errorText", t),
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <div className="p-7 radius-3xl surface-base-fill">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-body-bold-lg content-base-primary">{t("overview.description.title")}</h3>
          {canEdit && !isEditing && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="radius-2xs! gap-1!"
              onClick={handleEditClick}>
              <Edit2 size={16} color="currentColor" />
              {t("overview.description.edit")}
            </Button>
          )}
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-3">
            <Input
              isTextarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder={t("overview.description.placeholder")}
              className="min-h-[120px]"
            />
            <div className="flex items-center gap-2 justify-end">
              <Button variant="secondary" size="md" onClick={handleCancel} disabled={isSaving}>
                {t("overview.description.cancel")}
              </Button>
              <Button variant="primary" size="md" onClick={handleSave} disabled={isSaving}>
                {isSaving ? t("overview.description.saving") : t("overview.description.save")}
              </Button>
            </div>
          </div>
        ) : description ? (
          <p className="text-body-regular-md content-base-secondary whitespace-pre-wrap">{description}</p>
        ) : (
          <p className="text-body-regular-md content-base-low">{t("overview.description.empty")}</p>
        )}
      </div>

      {prompt && (
        <Prompt
          onClose={() => setPrompt(null)}
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          namespace="BoardPage"
        />
      )}
    </>
  );
}

